import React from 'react';
import { FlaskConical, FileText } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

// "2605230,2605231,2605232,2605240" -> ["2605230 a 2605232", "2605240"]; non-numeric kept as-is
export const compressNumbers = (values) => {
  const unique = [...new Set(values)];
  const numeric = unique.filter((v) => /^\d+$/.test(v)).sort((a, b) => (BigInt(a) < BigInt(b) ? -1 : 1));
  const others = unique.filter((v) => !/^\d+$/.test(v)).sort();
  const ranges = [];
  let start = null;
  let prev = null;
  for (const n of numeric) {
    if (prev !== null && BigInt(n) === BigInt(prev) + BigInt(1)) {
      prev = n;
      continue;
    }
    if (start !== null) ranges.push(start === prev ? start : `${start} a ${prev}`);
    start = n;
    prev = n;
  }
  if (start !== null) ranges.push(start === prev ? start : `${start} a ${prev}`);
  return [...ranges, ...others];
};

const STYLES = {
  salida: { box: 'bg-blue-50 border-blue-200 hover:bg-blue-100', title: 'text-blue-700', Icon: FlaskConical, icon: 'text-blue-600', label: 'Salida Cámara' },
  informe: { box: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100', title: 'text-emerald-700', Icon: FileText, icon: 'text-emerald-600', label: 'Informe' },
};

const CardBody = ({ group }) => {
  const s = STYLES[group.type];
  const nums = group.samples.map((x) => x.num_ensayo).filter(Boolean);
  const sinNum = group.samples.filter((x) => !x.num_ensayo).length;
  return (
    <div className="flex items-start gap-1.5">
      <s.Icon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${s.icon}`} />
      <div className="min-w-0">
        <p className={`text-xs font-semibold leading-tight truncate ${s.title}`} title={group.client}>{group.client}</p>
        <p className="text-[10px] uppercase tracking-wide text-gray-400">{s.label}{group.samples.length > 1 ? ` · ${group.samples.length} muestras` : ''}</p>
        <div className="mt-1 space-y-0.5" data-testid="event-goya-numbers">
          {compressNumbers(nums).map((r) => <p key={r} className="text-xs text-gray-700">{r}</p>)}
          {sinNum > 0 && <p className="text-xs text-gray-400 italic">Sin Nº GOYA ({sinNum})</p>}
        </div>
      </div>
    </div>
  );
};

export const EventGroupCard = ({ group, onOpen, testId }) => {
  const cls = `w-full text-left rounded-md p-2.5 transition-[box-shadow,transform,background-color] hover:shadow-md hover:scale-[1.02] cursor-pointer border ${STYLES[group.type].box}`;
  if (group.samples.length === 1) {
    return (
      <button type="button" onClick={() => onOpen(group.samples[0])} className={cls} data-testid={testId}>
        <CardBody group={group} />
      </button>
    );
  }
  const sorted = [...group.samples].sort((a, b) => (a.num_ensayo || '').localeCompare(b.num_ensayo || ''));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className={cls} data-testid={testId}><CardBody group={group} /></button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1 max-h-72 overflow-auto" data-testid={`${testId}-list`}>
        <p className="px-2 py-1 text-xs font-semibold text-gray-500">Elige la muestra a editar</p>
        {sorted.map((sample) => (
          <button key={sample.id} type="button" onClick={() => onOpen(sample)} className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-100 text-sm" data-testid={`event-sample-option-${sample.id}`}>
            <span className="font-medium">{sample.num_ensayo || 'Sin Nº GOYA'}</span>
            {sample.product && <span className="text-gray-500"> · {sample.product}</span>}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};
